/**
 * The Email helper's two-way switches: EmailModeToggle (Care Management ↔
 * SPICE, shared with the Assessment tool) and EmailTaskToggle (Draft an
 * email ↔ Just spelling and grammar). One shape, so the controls read as
 * one family on every screen size.
 *
 * A soft rounded pill with a knob that glides between the two labels on a
 * gentle ease. Care Management is sage (the default Peace tool color);
 * SPICE carries the pepper-red brand accent + a tiny pepper glyph. The
 * SPICE side is joyful and calming, matching the SPICE UX spirit.
 *
 * Accessibility: a single `role="switch"` button (off = Care Management,
 * on = SPICE), keyboard-operable natively (Space/Enter toggle a <button>),
 * with a dynamic aria-label that names the active mode. Honors
 * prefers-reduced-motion: no glide when the user asked for reduced motion.
 *
 * The pepper glyph reuses the brand SPICE mark from src/lib/hubLogos.ts
 * (full-color SVG), rendered the way HubBrandMark renders the brand marks.
 */

import { type ReactNode, useMemo } from 'react';
import { SPICE_PEPPER_SVG } from '@/lib/hubLogos';

export type EmailMode = 'care_management' | 'spice';

export interface EmailModeToggleProps {
  mode: EmailMode;
  onChange: (mode: EmailMode) => void;
  disabled?: boolean;
  // X.3.5.4: the toggle is now shared across the Email + Assessment tools. The
  // visual treatment is identical; these optional props let each tool supply
  // its own context noun (aria-label) and test id. (X.3.5.5: the per-mode hint
  // copy under the toggle was removed; the modes are self-evident.)
  contextNoun?: string;
  testId?: string;
}

// Mirror of the reduced-motion check in src/components/hub/HubQuote.tsx.
// Defined locally (tiny, and the two call sites evolve independently).
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// The SPICE side carries the terracotta pepper brand accent (--color-pepper,
// #C1512D — X.6.1). The active knob fills with pepper and the active "SPICE"
// label is white, mirroring the CM side (white on the dark sage knob); the
// former deep-red label was tuned for an earlier pale-rose knob and would not
// hold contrast on the dark terracotta fill. The pepper glyph artwork is the
// same terracotta family as the knob (base #C1512D; settled 2026-07-02, it was
// crimson #C71B23 before), so on the active knob it reads as a quiet tonal
// mark carried by its stem, shadow, cheeks, and smile; on the sand track it
// reads in full. If the active state ever needs more separation, that is a
// knob-shade or glyph-variant call for the design-system side. Sage-600 is the default
// Peace primary for the CM side (hex retained; per-surface token migration
// lands with the SPICE surface PR, not this token-foundation PR).
const SAGE_600 = 'var(--color-sage-600)';

/**
 * The one switch shape both Email helper controls share: a soft pill, two
 * labels, a knob that glides to the active half. `on` is the right-hand
 * side; the knob is pepper when the right side is the SPICE face, or when
 * the control sits on the SPICE face (`spice`), and sage otherwise.
 */
function TwoWaySwitch({
  on,
  onToggle,
  disabled,
  label,
  ariaLabel,
  testId,
  left,
  right,
  spice,
}: {
  on: boolean;
  onToggle: () => void;
  disabled: boolean;
  label: string;
  ariaLabel: string;
  testId: string;
  left: ReactNode;
  right: ReactNode;
  spice: boolean;
}): ReactNode {
  const reduceMotion = useMemo(prefersReducedMotion, []);
  return (
    <div>
      <span className="block text-sm font-medium text-ink mb-1">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={onToggle}
        data-testid={testId}
        className="relative grid grid-cols-2 w-full max-w-sm min-h-11 p-1 rounded-full bg-sand border border-sage-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 disabled:opacity-50"
      >
        {/* Sliding knob: covers the active half; glides on a ~200ms ease
            unless reduced motion is requested. */}
        <span
          aria-hidden="true"
          className={
            'absolute top-1 bottom-1 left-1 w-[calc(50%-0.25rem)] rounded-full shadow-sm' +
            (reduceMotion ? '' : ' transition-transform duration-200 ease-out')
          }
          style={{
            transform: on ? 'translateX(100%)' : 'translateX(0)',
            backgroundColor: spice ? 'var(--color-pepper)' : SAGE_600,
          }}
        />
        <span
          className={
            'relative z-10 flex items-center justify-center gap-1 px-3 text-sm font-semibold rounded-full ' +
            (on ? 'text-ink-soft' : 'text-white')
          }
        >
          {left}
        </span>
        <span
          className={
            'relative z-10 flex items-center justify-center gap-1.5 px-3 text-sm font-semibold rounded-full ' +
            (on ? 'text-white' : 'text-ink-soft')
          }
        >
          {right}
        </span>
      </button>
    </div>
  );
}

export function EmailModeToggle({
  mode,
  onChange,
  disabled = false,
  contextNoun = 'Email',
  testId = 'email-mode-toggle',
}: EmailModeToggleProps): ReactNode {
  const isSpice = mode === 'spice';
  return (
    <TwoWaySwitch
      on={isSpice}
      onToggle={() => onChange(isSpice ? 'care_management' : 'spice')}
      disabled={disabled}
      label="Mode"
      ariaLabel={`${contextNoun} mode: ${isSpice ? 'SPICE' : 'Care Management'}. Switch to ${isSpice ? 'Care Management' : 'SPICE'}.`}
      testId={testId}
      spice={isSpice}
      left="Care Management"
      right={
        <>
          <span
            aria-hidden="true"
            className="inline-block h-3.5 [&>svg]:h-full [&>svg]:w-auto"
            dangerouslySetInnerHTML={{ __html: SPICE_PEPPER_SVG }}
          />
          SPICE
        </>
      }
    />
  );
}

export type EmailTask = 'draft' | 'polish';

export interface EmailTaskToggleProps {
  task: EmailTask;
  onChange: (task: EmailTask) => void;
  /** The control sits on the SPICE face, so its knob is pepper. */
  spice?: boolean;
  disabled?: boolean;
}

/**
 * What the Email helper is for right now: drafting an email from notes,
 * or polishing one the writer already wrote. The same switch shape as
 * Mode, so the two read as one control family on every screen size.
 */
export function EmailTaskToggle({
  task,
  onChange,
  spice = false,
  disabled = false,
}: EmailTaskToggleProps): ReactNode {
  const isPolish = task === 'polish';
  return (
    <TwoWaySwitch
      on={isPolish}
      onToggle={() => onChange(isPolish ? 'draft' : 'polish')}
      disabled={disabled}
      label="What do you need"
      ariaLabel={`Email helper task: ${isPolish ? 'Just spelling and grammar' : 'Draft an email'}. Switch to ${isPolish ? 'Draft an email' : 'Just spelling and grammar'}.`}
      testId="email-task-toggle"
      spice={spice}
      left="Draft an email"
      right="Just spelling and grammar"
    />
  );
}
